/**
 * Pure reporting-tree arithmetic for the staff admin page. The backend enforces acyclicity on
 * write; these helpers must merely never hang or lose people if handed imperfect data (a manager
 * outside the fetched window, or a legacy cycle) — anyone unreachable renders as a top-level root.
 */

export interface StaffLike {
    id: number;
    managerId?: number | null;
    firstName: string;
    lastName?: string | null;
}

export interface StaffTreeNode<T extends StaffLike = StaffLike> {
    staff: T;
    depth: number;
    children: StaffTreeNode<T>[];
}

const byName = (a: StaffLike, b: StaffLike) =>
    `${a.firstName} ${a.lastName ?? ''}`.localeCompare(`${b.firstName} ${b.lastName ?? ''}`) || a.id - b.id;

/**
 * Roots-first tree. A member is a root when they report to nobody, or to someone missing from
 * `rows`. Cycle members (unreachable from any root) are surfaced as extra roots rather than
 * silently dropped — the page must show every person exactly once, whatever the data says.
 */
export const buildStaffTree = <T extends StaffLike>(rows: T[]): StaffTreeNode<T>[] => {
    const present = new Set(rows.map((r) => r.id));
    const childrenOf = new Map<number, T[]>();
    const roots: T[] = [];

    for (const row of [...rows].sort(byName)) {
        if (row.managerId != null && present.has(row.managerId) && row.managerId !== row.id) {
            const list = childrenOf.get(row.managerId) ?? [];
            list.push(row);
            childrenOf.set(row.managerId, list);
        } else {
            roots.push(row);
        }
    }

    const visited = new Set<number>();
    const build = (staff: T, depth: number): StaffTreeNode<T> | null => {
        if (visited.has(staff.id)) return null;
        visited.add(staff.id);
        const children = (childrenOf.get(staff.id) ?? [])
            .map((c) => build(c, depth + 1))
            .filter((n): n is StaffTreeNode<T> => n !== null);
        return { staff, depth, children };
    };

    const tree = roots.map((r) => build(r, 0)).filter((n): n is StaffTreeNode<T> => n !== null);

    // Anyone not reached from a root sits on a cycle island: lift each unvisited member out as a
    // root and walk whatever hangs beneath them (the visited set stops the loop closing).
    for (const row of [...rows].sort(byName)) {
        if (!visited.has(row.id)) {
            const node = build(row, 0);
            if (node) tree.push(node);
        }
    }
    return tree;
};

/** Pre-order flatten for indented rendering: parents immediately above their teams. */
export const flattenStaffTree = <T extends StaffLike>(tree: StaffTreeNode<T>[]): StaffTreeNode<T>[] => {
    const out: StaffTreeNode<T>[] = [];
    const walk = (n: StaffTreeNode<T>) => {
        out.push(n);
        n.children.forEach(walk);
    };
    tree.forEach(walk);
    return out;
};

/**
 * Every id in `rootId`'s subtree, itself included — the ids the Reports-to selector must EXCLUDE
 * (assigning me under my own team is the cycle the backend would reject; don't offer it).
 */
export const subtreeIdSet = (rows: StaffLike[], rootId: number): Set<number> => {
    const childrenOf = new Map<number, number[]>();
    for (const row of rows) {
        if (row.managerId != null) {
            const list = childrenOf.get(row.managerId) ?? [];
            list.push(row.id);
            childrenOf.set(row.managerId, list);
        }
    }
    const out = new Set<number>([rootId]);
    const queue = [rootId];
    while (queue.length) {
        for (const child of childrenOf.get(queue.shift()!) ?? []) {
            if (!out.has(child)) {
                out.add(child);
                queue.push(child);
            }
        }
    }
    return out;
};
